import { Plus, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTenant } from '@/context';
import { useWorkflows } from '@/hooks';
import { useIsReadOnly } from '@/hooks/use-subscription';

export function NewCaseDropdown() {
    const navigate = useNavigate();
    const { tenantId } = useTenant();
    const { data: workflowsData, isLoading, error } = useWorkflows();
    // Presentation only (ESCLOUD-288): the server refuses case creation for a
    // read-only tenant regardless; this just says so before the attempt.
    const readOnly = useIsReadOnly(tenantId);

    const workflows = workflowsData?.items ?? [];

    const handleSelectWorkflow = (workflowId: string) => {
        navigate(`/tenants/${tenantId}/cases/new?workflow=${workflowId}`);
    };

    if (readOnly) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    {/* span wrapper: a disabled button emits no pointer events */}
                    <span tabIndex={0}>
                        <Button disabled aria-disabled>
                            <Plus className="mr-2 h-4 w-4" />
                            New Case
                        </Button>
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    Your workspace is read-only — restore the subscription to create new cases.
                </TooltipContent>
            </Tooltip>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    New Case
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Select Workflow</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-sm text-muted-foreground">Loading workflows...</span>
                    </div>
                ) : error ? (
                    <div className="px-2 py-4 text-center text-sm text-destructive">
                        Failed to load workflows
                    </div>
                ) : workflows.length === 0 ? (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                        No workflows available
                    </div>
                ) : (
                    workflows.map((workflow) => (
                        <DropdownMenuItem
                            key={workflow.id}
                            onClick={() => handleSelectWorkflow(workflow.id)}
                            className="cursor-pointer"
                        >
                            <div className="flex flex-col gap-0.5">
                                <span className="font-medium">{workflow.name}</span>
                                {workflow.description && (
                                    <span className="text-xs text-muted-foreground line-clamp-1">
                                        {workflow.description}
                                    </span>
                                )}
                            </div>
                        </DropdownMenuItem>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
